import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaBell, FaSignOutAlt, FaUser, FaTimes } from 'react-icons/fa';
import { useNotifications } from '../hooks/useNotifications';

const AdminMobileHeader = () => {
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Only show for admin users on admin pages
  if (user?.role !== 'admin' || !location.pathname.startsWith('/admin')) {
    return null;
  }

  // Don't show on admin login page
  if (location.pathname === '/admin/login') {
    return null;
  }

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    logout();
    navigate('/admin/login');
    setShowLogoutConfirm(false);
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const handleNotificationsClick = () => {
    navigate('/notifications');
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const getPageTitle = () => {
    if (location.pathname === '/admin' || location.pathname === '/admin/') {
      return 'Dashboard';
    }
    if (location.pathname.startsWith('/admin/users')) {
      return 'Users';
    }
    if (location.pathname.startsWith('/admin/trips')) {
      return 'Trips';
    }
    if (location.pathname.startsWith('/admin/bookings')) {
      return 'Bookings';
    }
    if (location.pathname.startsWith('/admin/payouts')) {
      return 'Payouts';
    }
    if (location.pathname.startsWith('/admin/analytics')) {
      return 'Analytics';
    }
    return 'Admin Panel';
  };

  return (
    <header className="bg-gradient-to-r from-blue-600 to-blue-800 text-white sticky top-0 z-40 md:hidden">
      <div className="flex items-center justify-between px-4 py-3">
        {/* Left: Page Title */}
        <div className="flex items-center">
          <h1 className="text-lg font-bold">{getPageTitle()}</h1>
        </div>

        {/* Right: Action Icons */}
        <div className="flex items-center space-x-2">
          {/* Notifications */}
          <button
            onClick={handleNotificationsClick}
            className="relative p-2 rounded-full hover:bg-blue-700 transition-colors"
            aria-label="Notifications"
          >
            <FaBell className="text-lg" />
            {unreadCount > 0 && (
              <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Profile */}
          <button
            onClick={handleProfileClick}
            className="p-2 rounded-full hover:bg-blue-700 transition-colors"
            aria-label="Profile"
          >
            <FaUser className="text-lg" />
          </button>

          {/* Logout */}
          <button
            onClick={handleLogoutClick}
            className="p-2 rounded-full hover:bg-blue-700 transition-colors"
            aria-label="Logout"
          >
            <FaSignOutAlt className="text-lg" />
          </button>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Confirm Logout</h3>
                <button
                  onClick={cancelLogout}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close"
                >
                  <FaTimes className="text-xl" />
                </button>
              </div>
              <p className="text-gray-600 mb-6">
                Are you sure you want to logout? You will need to login again to access the admin panel.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={cancelLogout}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmLogout}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default AdminMobileHeader;

